import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

const GOLD = '#c8a84b';
const BROWN_DARK = '#3b2000';
const BROWN_MID = '#5a3e10';
const BROWN_LIGHT = '#8b6914';
const BG = '#faf6ef';

// -- מפריד מעוטר ---------------------------------------------------------------
const OrnamentalDivider = ({ symbol = '✦', compact = false }) => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    my: compact ? 0.5 : 0.75,
    width: '100%',
    maxWidth: 400,
    mx: 'auto',
  }}>
    <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, transparent, ${GOLD})` }} />
    <Typography sx={{ color: GOLD, fontSize: compact ? '0.55rem' : '0.6rem', lineHeight: 1, opacity: 0.8 }}>{symbol}</Typography>
    <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, ${GOLD}, transparent)` }} />
  </Box>
);

// -- כותרת קטגוריה מעוטרת ------------------------------------------------------
const CategoryHeader = ({ label }) => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    mb: 0.5,
    justifyContent: 'center',
  }}>
    <Box sx={{ flex: 1, maxWidth: 50, height: '1px', background: `linear-gradient(90deg, transparent, ${GOLD}80)` }} />
    <Typography sx={{
      fontFamily: 'Frank Ruhl Libre, David, serif',
      fontSize: '0.58rem',
      color: BROWN_LIGHT,
      letterSpacing: '0.1em',
      whiteSpace: 'nowrap',
      fontWeight: 600,
    }}>
      ✦ {label} ✦
    </Typography>
    <Box sx={{ flex: 1, maxWidth: 50, height: '1px', background: `linear-gradient(90deg, ${GOLD}80, transparent)` }} />
  </Box>
);

// -- מיפוי קטגוריות לשמות עבריים -----------------------------------------------
const CATEGORY_LABELS = {
  tachanun:            'תחנון',
  hallel:              'הלל',
  prayerAdditions:     'תוספות תפילה',
  seasonalPrayers:     'תפילות עונתיות',
  seasonalTransitions: 'מעברים עונתיים',
  selichot:            'סליחות',
  weekdayPrayers:      'תפילות ימי חול',
  shabbatAdditions:    'תוספות שבת',
  motzaeiShabbat:      'מוצאי שבת',
  seasonalCustoms:     'מנהגים עונתיים',
  liturgy:             'פיוטים וקריאות',
  leyning:             'קריאת התורה',
  haftarah:            'הפטרה',
  mourningPeriods:     'ימי אבל ותענית',
  general:             'כללי',
};

// -- פריט בודד -----------------------------------------------------------------
const ItemRow = ({ item }) => {
  const isHighPriority = item.priority === 'high';

  return (
    <Box sx={{
      py: 0.4,
      px: 1,
      textAlign: 'center',
    }}>
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, David, serif',
        fontSize: isHighPriority ? '0.82rem' : '0.75rem',
        fontWeight: isHighPriority ? 700 : 500,
        color: isHighPriority ? BROWN_DARK : BROWN_MID,
        lineHeight: 1.6,
        direction: 'rtl',
      }}>
        {item.label}
        {item.value ? ` — ${item.value}` : ''}
      </Typography>
      {item.detail && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.6rem',
          color: `${BROWN_LIGHT}b3`,
          lineHeight: 1.4,
          fontStyle: 'italic',
          direction: 'rtl',
        }}>
          {item.detail}
        </Typography>
      )}
    </Box>
  );
};

// -- עזר: קיבוץ לפי קטגוריה ---------------------------------------------------
function groupByCategory(items) {
  const map = {};
  items.forEach((item) => {
    const cat = item.category || 'general';
    if (!map[cat]) map[cat] = [];
    map[cat].push(item);
  });
  return Object.entries(map);
}

// -- יום lookahead בודד --------------------------------------------------------
const LookaheadDay = ({ dayData }) => (
  <Box sx={{ mt: 1 }}>
    <OrnamentalDivider symbol="✧" />
    <Typography sx={{
      fontFamily: 'Frank Ruhl Libre, David, serif',
      fontSize: '0.78rem',
      fontWeight: 700,
      color: BROWN_LIGHT,
      textAlign: 'center',
      mb: 0.5,
      letterSpacing: '0.03em',
    }}>
      {dayData.hebrewDate?.hebrew || dayData.date}
    </Typography>
    {dayData.items.length === 0 ? (
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, David, serif',
        fontSize: '0.68rem',
        color: `${BROWN_LIGHT}99`,
        textAlign: 'center',
        fontStyle: 'italic',
      }}>
        — אין ענייני יום מיוחדים —
      </Typography>
    ) : (
      groupByCategory(dayData.items).map(([cat, catItems]) => (
        <Box key={cat} sx={{ mb: 1 }}>
          <CategoryHeader label={CATEGORY_LABELS[cat] || cat} />
          {catItems.map((item, i) => (
            <ItemRow key={item.id || `${cat}-${i}`} item={item} />
          ))}
        </Box>
      ))
    )}
  </Box>
);

// == תבנית קלף =================================================================
const Parchment = ({
  today,
  items,
  lookaheadItems,
  lookaheadDays,
  loading,
  error,
  title,
  showTitle,
}) => {
  const hebrewDateStr = today?.hebrewDate?.hebrew || '';
  const grouped = groupByCategory(items || []);

  return (
    <Box sx={{
      background: BG,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      direction: 'rtl',
      overflowY: 'auto',
      padding: '18px 16px',
      position: 'relative',
      // גבול כפול מעוטר
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 5,
        border: `2px solid ${GOLD}`,
        borderRadius: 1,
        pointerEvents: 'none',
        opacity: 0.5,
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 9,
        border: `1px solid ${GOLD}`,
        borderRadius: 0.5,
        pointerEvents: 'none',
        opacity: 0.25,
      },
      '&::-webkit-scrollbar': { width: 3 },
      '&::-webkit-scrollbar-thumb': { background: `${GOLD}50`, borderRadius: 2 },
    }}>
      {/* כותרת */}
      {showTitle && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.7rem',
          color: BROWN_LIGHT,
          letterSpacing: '0.12em',
          mt: 0.5,
          position: 'relative',
          zIndex: 1,
          fontWeight: 600,
        }}>
          {title}
        </Typography>
      )}

      {/* תאריך עברי */}
      {hebrewDateStr && (
        <>
          <OrnamentalDivider />
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '1rem',
            fontWeight: 700,
            color: BROWN_DARK,
            textAlign: 'center',
            letterSpacing: '0.04em',
            position: 'relative',
            zIndex: 1,
          }}>
            {hebrewDateStr}
          </Typography>
          <OrnamentalDivider />
        </>
      )}

      {/* loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1, position: 'relative', zIndex: 1 }}>
          <CircularProgress size={18} sx={{ color: GOLD }} />
        </Box>
      )}

      {/* error */}
      {error && (
        <Typography sx={{
          fontSize: '0.68rem',
          color: '#b45309',
          textAlign: 'center',
          mb: 0.5,
          position: 'relative',
          zIndex: 1,
        }}>
          שגיאה בטעינה
        </Typography>
      )}

      {/* ריק */}
      {grouped.length === 0 && !loading && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.78rem',
          color: `${BROWN_LIGHT}99`,
          textAlign: 'center',
          mt: 2,
          fontStyle: 'italic',
          position: 'relative',
          zIndex: 1,
        }}>
          — אין ענייני יום מיוחדים להיום —
        </Typography>
      )}

      {/* פריטי היום לפי קטגוריות */}
      <Box sx={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}>
        {grouped.map(([cat, catItems], gi) => (
          <Box key={cat} sx={{ mb: 0.5 }}>
            <CategoryHeader label={CATEGORY_LABELS[cat] || cat} />
            {catItems.map((item, i) => (
              <ItemRow key={item.id || `${cat}-${i}`} item={item} />
            ))}
            {gi < grouped.length - 1 && (
              <Box sx={{ my: 0.5 }} />
            )}
          </Box>
        ))}
      </Box>

      {/* ימי lookahead */}
      {lookaheadDays > 0 && lookaheadItems && lookaheadItems.length > 0 && (
        <Box sx={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}>
          {lookaheadItems.map((dayData, i) => (
            <LookaheadDay key={dayData.date || i} dayData={dayData} />
          ))}
        </Box>
      )}

      {/* מפריד סיום */}
      <OrnamentalDivider />
    </Box>
  );
};

export default Parchment;
